import { ArchitectureParseError } from "../errors";
import { splitH2Sections } from "../parse-sections";
import type { ArchitectureData, Meta } from "../types";
import { parseDecisionsV3 } from "../v3/parse-decisions-v3";
import { parseL2 } from "../v3/parse-l2-v3";
import { parseL3 } from "../v3/parse-l3-v3";
import { parseOperationalNotesV3 } from "../v3/parse-operational-notes-v3";
import { parseQuestionsV3 } from "../v3/parse-questions-v3";
import { parseL1V4 } from "./parse-l1-v4";

// archviz/v0.4 entry point. v0.4 is additive over v0.3: only L1 external
// system parsing changes (optional `permissions:` and `endpoints:` blocks).
// Everything else delegates to v3 — L2 containers, L3 components,
// decisions, questions, operational notes are unchanged.

const LEVEL_PREFIX = /^l[1-3]\s*[—\-–:]?\s*/i;

type Handler = "l1" | "l2" | "l3" | "decisions" | "questions" | "ops";

const SECTION_HANDLERS: Record<string, Handler> = {
  "system context": "l1",
  containers: "l2",
  components: "l3",
  decisions: "decisions",
  questions: "questions",
  "operational notes": "ops",
};

const RECOGNIZED_TITLES = [
  "## L1 — System Context",
  "## L2 — Containers",
  "## L3 — Components",
  "## Decisions",
  "## Questions",
  "## Operational Notes",
];

export function parseArchitectureV4(meta: Meta, body: string): ArchitectureData {
  const result: ArchitectureData = {
    meta,
    actors: [],
    externalSystems: [],
    containers: [],
    components: [],
    questions: [],
    dataFlows: [],
    decisions: [],
    operationalNotes: [],
  };

  const sections = splitH2Sections(body);
  let recognized = 0;
  for (const section of sections) {
    const normalized = section.title.toLowerCase().replace(LEVEL_PREFIX, "").trim();
    const handler = SECTION_HANDLERS[normalized];
    if (!handler) {
      console.warn(`[arch-parser v0.4] skipping unknown section: ${section.title}`);
      continue;
    }
    recognized += 1;
    dispatch(handler, section.body, result);
  }

  if (recognized === 0) {
    throw new ArchitectureParseError(
      [
        "archviz/v0.4 document has no recognized sections.",
        "",
        "Expected at least one of:",
        ...RECOGNIZED_TITLES.map((t) => `  - ${t}`),
        "",
        "Level prefixes accept em-dash (—), hyphen (-), colon, or just whitespace.",
        "See docs/architecture-template.md for the canonical format.",
      ].join("\n"),
    );
  }

  return result;
}

function dispatch(handler: Handler, body: string, result: ArchitectureData): void {
  switch (handler) {
    case "l1": {
      const { system, actors, externalSystems } = parseL1V4(body);
      result.system = system;
      result.actors = actors;
      result.externalSystems = externalSystems;
      break;
    }
    case "l2": {
      const { containers, dataFlows } = parseL2(body);
      result.containers = containers;
      result.dataFlows = dataFlows;
      break;
    }
    case "l3": {
      const components = parseL3(body);
      result.components = components;
      // Mirror into container.components so the existing renderer reads
      // them via the legacy nesting path until Phase 5 migrates it.
      for (const c of result.containers) c.components = [];
      const byContainer = new Map<string, typeof components>();
      for (const cmp of components) {
        const key = cmp.container ?? cmp.containerId;
        if (!key) continue;
        const bucket = byContainer.get(key) ?? [];
        bucket.push(cmp);
        byContainer.set(key, bucket);
      }
      for (const c of result.containers) {
        c.components = byContainer.get(c.id) ?? [];
      }
      break;
    }
    case "decisions":
      result.decisions = parseDecisionsV3(body);
      break;
    case "questions":
      result.questions = parseQuestionsV3(body);
      break;
    case "ops":
      result.operationalNotes = parseOperationalNotesV3(body);
      break;
  }
}
